/**
 * Utility functions for handling API errors consistently across the application
 */

export interface ErrorHandlerOptions {
  /** The action being performed (e.g., 'create', 'edit', 'delete', 'upload') */
  action?: string;
  /** The type of item being acted upon (e.g., 'place', 'media', 'item') */
  itemType?: string;
  /** Custom message to show for 403 errors */
  custom403Message?: string;
}

/**
 * Check if an error is a 403 Forbidden error
 */
export const is403Error = (error: unknown): boolean => {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return message.includes('403') || message.includes('forbidden');
  }
  return false;
};

/**
 * Check if an error is a 401 Unauthorized error
 */
export const is401Error = (error: unknown): boolean => {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return message.includes('401') || message.includes('unauthorized');
  }
  return false;
};

/**
 * Generate a user-friendly error message for 403 errors
 */
export const get403ErrorMessage = (options: ErrorHandlerOptions = {}): string => {
  const { action = 'perform this action', itemType = 'items' } = options;
  
  if (options.custom403Message) {
    return options.custom403Message;
  }
  
  return `You don't have permission to ${action} ${itemType}. You have view-only access.`;
};

/**
 * Generate a user-friendly error message for 401 errors
 */
export const get401ErrorMessage = (): string => {
  return "Your session has expired. Please log in again.";
};

/**
 * Handle API errors with appropriate user messages
 * Returns an object with the error type and message to display
 */
export const handleApiError = (error: unknown, options: ErrorHandlerOptions = {}) => {
  if (is401Error(error)) {
    return {
      type: 'session_expired' as const,
      message: get401ErrorMessage(),
      variant: 'error' as const
    };
  }
  
  if (is403Error(error)) {
    return {
      type: 'permission_denied' as const,
      message: get403ErrorMessage(options),
      variant: 'warning' as const
    };
  }
  
  // Generic error handling
  const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
  const { action = 'perform action', itemType = 'item' } = options;
  
  return {
    type: 'generic_error' as const,
    message: `Failed to ${action} ${itemType}: ${errorMessage}`,
    variant: 'error' as const
  };
};


/**
 * Common error handler for form submissions
 */
export const handleFormSubmissionError = (
  error: unknown, 
  enqueueSnackbar: (message: string, options: { variant: 'error' | 'warning' | 'success' }) => void,
  options: ErrorHandlerOptions = {}
) => {
  const { message, variant } = handleApiError(error, options);
  enqueueSnackbar(message, { variant });
};


